// Константы для форматирования валют
export const CURRENCY_SYMBOLS = {
  UAH: "₴",
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CNY: "¥",
  RUB: "₽",
  INR: "₹",
  CAD: "C$",
  AUD: "A$",
  BRL: "R$",
  CHF: "Fr",
  PLN: "zł",
  TRY: "₺",
  ZAR: "R",
  IDR: "Rp",
  KRW: "₩"
};

/**
 * Форматирует сумму в соответствии с валютой
 * @param {number|string} amount - Сумма для форматирования
 * @param {string} currency - Код валюты
 * @returns {string} Отформатированная строка с суммой и символом валюты
 */
export const formatCurrency = (amount, currency = 'UAH') => {
  // Проверка на корректность суммы
  if (amount === null || amount === undefined || isNaN(parseFloat(amount))) {
    amount = 0;
  }
  
  // Преобразование в число
  const numericAmount = parseFloat(String(amount).replace(/[^\d.-]/g, ''));
  
  // Форматирование в зависимости от валюты
  let formattedAmount;
  
  // Для валют без дробной части (JPY, KRW, IDR и т.д.)
  if (['JPY', 'KRW', 'IDR'].includes(currency)) {
    formattedAmount = Math.round(numericAmount).toLocaleString();
  } else {
    formattedAmount = numericAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  
  // Расположение символа валюты
  if (['USD', 'CAD', 'AUD', 'BRL'].includes(currency)) {
    return `${symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount} ${symbol}`;
  }
};

/**
 * Конвертирует сумму из одной валюты в другую
 * @param {number} amount - Сумма для конвертации
 * @param {string} fromCurrency - Исходная валюта
 * @param {string} toCurrency - Целевая валюта
 * @param {Object} exchangeRates - Объект с курсами валют
 * @returns {number} Сконвертированная сумма
 */
// Добавьте или обновите константу FALLBACK_RATES
export const FALLBACK_RATES = {
  'USD_UAH': 38.5,
  'EUR_UAH': 41.5,
  'UAH_USD': 0.026,
  'UAH_EUR': 0.024,
  'EUR_USD': 1.06,
  'USD_EUR': 0.94,
  'USD_RUB': 75.5,
  'UAH_RUB': 1.9,
  'EUR_RUB': 80.2,
  'GBP_UAH': 48.2,
  'UAH_GBP': 0.0207,
  'GBP_USD': 1.25,
  'USD_GBP': 0.8,
  'GBP_EUR': 1.18,
  'EUR_GBP': 0.85
};

// Обновите функцию convertCurrency для более надежной работы
export const convertCurrency = (amount, fromCurrency, toCurrency, exchangeRates = {}) => {
  // Проверяем и логируем входные данные
  console.log(`Converting ${amount} from ${fromCurrency} to ${toCurrency}`);
  console.log("Available rates:", Object.keys(exchangeRates).slice(0, 5), "...");
  
  if (fromCurrency === toCurrency) return amount;
  
  if (amount === null || amount === undefined || isNaN(parseFloat(amount))) {
    console.warn("Invalid amount for conversion:", amount);
    return 0;
  }
  
  // Нормализуем сумму
  const numericAmount = parseFloat(amount);
  
  // Проверяем наличие прямого курса
  const directRateKey = `${fromCurrency}_${toCurrency}`;
  if (exchangeRates[directRateKey]) {
    console.log(`Using direct rate ${directRateKey}: ${exchangeRates[directRateKey]}`);
    return numericAmount * exchangeRates[directRateKey];
  }
  
  // Пробуем через USD
  const fromToUsd = exchangeRates[`${fromCurrency}_USD`];
  const usdToTarget = exchangeRates[`USD_${toCurrency}`];
  
  if (fromToUsd && usdToTarget) {
    console.log(`Converting via USD: ${fromCurrency} -> USD -> ${toCurrency}`);
    return numericAmount * fromToUsd * usdToTarget;
  }
  
  // Пробуем через UAH
  const fromToUah = exchangeRates[`${fromCurrency}_UAH`];
  const uahToTarget = exchangeRates[`UAH_${toCurrency}`];
  
  if (fromToUah && uahToTarget) {
    console.log(`Converting via UAH: ${fromCurrency} -> UAH -> ${toCurrency}`);
    return numericAmount * fromToUah * uahToTarget;
  }
  
  // Используем резервные курсы
  if (FALLBACK_RATES[directRateKey]) {
    console.warn(`Using fallback rate for ${directRateKey}: ${FALLBACK_RATES[directRateKey]}`);
    return numericAmount * FALLBACK_RATES[directRateKey];
  }
  
  // Пробуем резервные курсы через USD
  const fallbackFromToUsd = FALLBACK_RATES[`${fromCurrency}_USD`];
  const fallbackUsdToTarget = FALLBACK_RATES[`USD_${toCurrency}`];
  
  if (fallbackFromToUsd && fallbackUsdToTarget) {
    console.warn(`Using fallback rates via USD`);
    return numericAmount * fallbackFromToUsd * fallbackUsdToTarget;
  }
  
  // Если ничего не помогло, возвращаем исходную сумму
  console.warn(`Could not convert ${fromCurrency} to ${toCurrency}, returning original amount`);
  return numericAmount;
};

/**
 * Получает курсы валют с сервера
 * @returns {Promise<Object>} Объект с курсами валют
 */
export const fetchExchangeRates = async () => {
  try {
    const response = await fetch('https://fftg.farodinprojects.org:3000/exchange_rates');
    if (!response.ok) {
      throw new Error('Failed to fetch exchange rates');
    }
    const data = await response.json();
    return data.rates || {};
  } catch (error) {
    console.error('Error fetching exchange rates:', error);
    return {};
  }
};